import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { ZeitauswahlChange } from './zeitauswahl-change';
import { ZeitauswahlService } from './zeitauswahl.service';

@Component({
    selector: 'kiene-zeitauswahl',
    templateUrl: './zeitauswahl.component.html',
    styleUrls: ['./zeitauswahl.component.css'],
})
export class ZeitauswahlComponent implements OnInit {
    @Output('change') change = new EventEmitter<ZeitauswahlChange>();

    @Input('hideAggregate') hideAggregate = false;
    /** When true, the "Tag" interval toggle is hidden (e.g. Dashboard). */
    @Input('hideTag') hideTag = false;
    /** When true, dates after today cannot be selected and ranges are clamped to today. */
    @Input('disableFutureDates') disableFutureDates = false;

    startDateCtrl = new UntypedFormControl();
    endDateCtrl = new UntypedFormControl();
    aggregierenCtrl = new UntypedFormControl('woche');

    aktuellerZeitintervalInStunden = 24;
    aktuellerZeitinterval = 'monat';
    individuellInterval = 0;
    aktuellVon: Date;
    aktuellBis: Date;
    maxDate: Date | null = null;

    constructor(private service: ZeitauswahlService) {}

    dateFilter = (d: Date | null): boolean => {
        if (!this.disableFutureDates || !d) {
            return true;
        }
        const today = this.startOfDay(new Date());
        return this.startOfDay(d).getTime() <= today.getTime();
    };

    ngOnInit(): void {
        if (this.disableFutureDates) {
            this.maxDate = this.endOfDay(new Date());
        }

        let z = this.service.getZeitauswahl();

        this.aktuellVon = z.von;
        this.aktuellBis = z.bis;
        this.aggregierenCtrl.setValue(z.aggregieren);
        this.aktuellerZeitinterval = z.aktuellerZeitinterval || 'monat';
        if (this.hideTag && this.aktuellerZeitinterval === 'tag') {
            this.aktuellerZeitinterval = 'monat';
        }

        this.berechneStartUndEnde();

        this.aggregierenCtrl.valueChanges.subscribe(() => this.fireEvent());
    }

    fireEvent() {
        let event = new ZeitauswahlChange();
        event.von = this.aktuellVon;
        event.bis = this.aktuellBis;
        event.aggregieren = this.aggregierenCtrl.value;
        event.aktuellerZeitinterval = this.aktuellerZeitinterval;
        if (event.von && event.bis) {
            this.service.setZeitauswahl(event);
            this.change.emit(event);
        }
    }

    rewind() {
        if (this.aktuellerZeitinterval === 'tag') {
            this.aktuellVon = new Date(this.aktuellVon.setDate(this.aktuellVon.getDate() - 1));
            this.aktuellBis = new Date(this.aktuellBis.setDate(this.aktuellBis.getDate() - 1));
        } else if (this.aktuellerZeitinterval === 'woche') {
            // Snap to calendar week (Mo–So). Shifting clamped single-day ranges would stay broken.
            const week = this.getWoche(this.aktuellVon || this.aktuellBis);
            const pivot = new Date(week.von);
            pivot.setDate(pivot.getDate() - 7);
            const prev = this.getWoche(pivot);
            this.aktuellVon = prev.von;
            this.aktuellBis = prev.bis;
        } else if (this.aktuellerZeitinterval === 'monat') {
            this.aktuellVon = new Date(this.aktuellVon.setMonth(this.aktuellVon.getMonth() - 1));
            this.aktuellBis = new Date(this.aktuellVon.getFullYear(), this.aktuellVon.getMonth() + 1, 0);
        } else if (this.aktuellerZeitinterval === 'quartal') {
            // Immer auf Kalenderquartal snappen (sonst Drift bei Vor/Zurück)
            const q = this.getQuartal(this.aktuellVon);
            const pivot = new Date(q.von.getFullYear(), q.von.getMonth() - 1, 15);
            const prev = this.getQuartal(pivot);
            this.aktuellVon = prev.von;
            this.aktuellBis = prev.bis;
        } else if (this.aktuellerZeitinterval === 'halbjahr') {
            const h = this.getHalbjahr(this.aktuellVon);
            const pivot = new Date(h.von.getFullYear(), h.von.getMonth() - 1, 15);
            const prev = this.getHalbjahr(pivot);
            this.aktuellVon = prev.von;
            this.aktuellBis = prev.bis;
        } else if (this.aktuellerZeitinterval === 'jahr') {
            this.aktuellVon = new Date(this.aktuellVon.setMonth(this.aktuellVon.getMonth() - 12));
            this.aktuellBis = new Date(this.aktuellVon.getFullYear(), this.aktuellVon.getMonth() + 12, 0);
        } else if (this.aktuellerZeitinterval === 'zwei_jahre') {
            const shifted = this.shiftZweiJahre(this.aktuellVon, this.aktuellBis, -1);
            this.aktuellVon = shifted.von;
            this.aktuellBis = shifted.bis;
        } else if (this.aktuellerZeitinterval === 'individuell') {
            this.aktuellVon = new Date(this.aktuellVon.setDate(this.aktuellVon.getDate() - this.individuellInterval));
            this.aktuellBis = new Date(this.aktuellBis.setDate(this.aktuellBis.getDate() - this.individuellInterval));
        }

        this.clampToTodayIfNeeded();
        this.startDateCtrl.setValue(this.aktuellVon);
        this.endDateCtrl.setValue(this.aktuellBis);

        this.fireEvent();
    }

    forward() {
        const prevVon = new Date(this.aktuellVon);
        const prevBis = new Date(this.aktuellBis);

        if (this.aktuellerZeitinterval === 'tag') {
            this.aktuellVon = new Date(this.aktuellVon.setDate(this.aktuellVon.getDate() + 1));
            this.aktuellBis = new Date(this.aktuellBis.setDate(this.aktuellBis.getDate() + 1));
        } else if (this.aktuellerZeitinterval === 'woche') {
            const week = this.getWoche(this.aktuellVon || this.aktuellBis);
            const pivot = new Date(week.von);
            pivot.setDate(pivot.getDate() + 7);
            const next = this.getWoche(pivot);
            this.aktuellVon = next.von;
            this.aktuellBis = next.bis;
        } else if (this.aktuellerZeitinterval === 'monat') {
            this.aktuellVon = new Date(this.aktuellVon.setMonth(this.aktuellVon.getMonth() + 1));
            this.aktuellBis = new Date(this.aktuellVon.getFullYear(), this.aktuellVon.getMonth() + 1, 0);
        } else if (this.aktuellerZeitinterval === 'quartal') {
            const q = this.getQuartal(this.aktuellVon);
            const pivot = new Date(q.bis.getFullYear(), q.bis.getMonth() + 1, 15);
            const next = this.getQuartal(pivot);
            this.aktuellVon = next.von;
            this.aktuellBis = next.bis;
        } else if (this.aktuellerZeitinterval === 'halbjahr') {
            const h = this.getHalbjahr(this.aktuellVon);
            const pivot = new Date(h.bis.getFullYear(), h.bis.getMonth() + 1, 15);
            const next = this.getHalbjahr(pivot);
            this.aktuellVon = next.von;
            this.aktuellBis = next.bis;
        } else if (this.aktuellerZeitinterval === 'jahr') {
            this.aktuellVon = new Date(this.aktuellVon.setMonth(this.aktuellVon.getMonth() + 12));
            this.aktuellBis = new Date(this.aktuellVon.getFullYear(), this.aktuellVon.getMonth() + 12, 0);
        } else if (this.aktuellerZeitinterval === 'zwei_jahre') {
            const shifted = this.shiftZweiJahre(this.aktuellVon, this.aktuellBis, 1);
            this.aktuellVon = shifted.von;
            this.aktuellBis = shifted.bis;
        } else if (this.aktuellerZeitinterval === 'individuell') {
            this.aktuellVon = new Date(this.aktuellVon.setDate(this.aktuellVon.getDate() + this.individuellInterval));
            this.aktuellBis = new Date(this.aktuellBis.setDate(this.aktuellBis.getDate() + this.individuellInterval));
        }

        this.clampToTodayIfNeeded();
        // Do not jump into a fully future range (e.g. next month while still mid-month).
        if (this.disableFutureDates && this.startOfDay(this.aktuellVon).getTime() > this.startOfDay(new Date()).getTime()) {
            this.aktuellVon = prevVon;
            this.aktuellBis = prevBis;
            return;
        }
        this.startDateCtrl.setValue(this.aktuellVon);
        this.endDateCtrl.setValue(this.aktuellBis);

        this.fireEvent();
    }

    morgens(d: Date) {
        let date = new Date(d);
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        return date;
    }

    abends(d: Date) {
        let date = new Date(d);
        date.setHours(23);
        date.setMinutes(59);
        date.setSeconds(59);
        return date;
    }

    montag() {
        return this.getWoche(this.aktuellBis || this.aktuellVon || new Date()).von;
    }

    /** Calendar week Monday 00:00 – Sunday 23:59:59 (local). */
    getWoche(d: Date): { von: Date; bis: Date } {
        const base = new Date(d);
        const dayOfWeek = base.getDay(); // 0=Sun … 6=Sat
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(base);
        monday.setDate(base.getDate() + mondayOffset);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        return { von: this.morgens(monday), bis: this.abends(sunday) };
    }

    erster(d: Date) {
        d.setDate(1);
        return d;
    }

    closedDatepicker() {
        this.aktuellerZeitinterval = 'individuell';
        this.aktuellVon = this.startDateCtrl.value;
        this.aktuellBis = this.endDateCtrl.value;
        this.clampToTodayIfNeeded();
        this.startDateCtrl.setValue(this.aktuellVon);
        this.endDateCtrl.setValue(this.aktuellBis);
        this.individuellInterval = this.differenzInTagen(this.aktuellVon, this.aktuellBis);
        this.fireEvent();
    }

    berechneStartUndEnde() {
        if (this.aktuellerZeitinterval === 'tag') {
            this.startDateCtrl.setValue(this.morgens(this.aktuellBis));
            this.endDateCtrl.setValue(this.abends(this.aktuellBis));
        } else if (this.aktuellerZeitinterval === 'woche') {
            const week = this.getWoche(this.aktuellBis || this.aktuellVon || new Date());
            this.startDateCtrl.setValue(week.von);
            this.endDateCtrl.setValue(week.bis);
        } else if (this.aktuellerZeitinterval === 'monat') {
            let erster = this.erster(this.aktuellBis);
            this.startDateCtrl.setValue(erster);
            this.endDateCtrl.setValue(new Date(erster.getFullYear(), erster.getMonth() + 1, 0));
        } else if (this.aktuellerZeitinterval === 'quartal') {
            let quartal = this.getQuartal(this.aktuellBis);
            this.startDateCtrl.setValue(quartal.von);
            this.endDateCtrl.setValue(quartal.bis);
        } else if (this.aktuellerZeitinterval === 'halbjahr') {
            let halbjahr = this.getHalbjahr(this.aktuellBis);
            this.startDateCtrl.setValue(halbjahr.von);
            this.endDateCtrl.setValue(halbjahr.bis);
        } else if (this.aktuellerZeitinterval === 'jahr') {
            let von = new Date(this.aktuellBis.getFullYear(), 0, 1);
            let bis = new Date(this.aktuellBis.getFullYear(), 11, 31);
            this.startDateCtrl.setValue(von);
            this.endDateCtrl.setValue(bis);
        } else if (this.aktuellerZeitinterval === 'zwei_jahre') {
            const range = this.getZweiJahre(this.aktuellBis);
            this.startDateCtrl.setValue(range.von);
            this.endDateCtrl.setValue(range.bis);
        } else if (this.aktuellerZeitinterval === 'individuell') {
            this.aktuellVon = this.startDateCtrl.value;
            this.aktuellBis = this.endDateCtrl.value;
            this.individuellInterval = this.differenzInTagen(this.aktuellVon, this.aktuellBis);
        }

        this.aktuellVon = this.startDateCtrl.value;
        this.aktuellBis = this.endDateCtrl.value;
        this.clampToTodayIfNeeded();
        this.startDateCtrl.setValue(this.aktuellVon);
        this.endDateCtrl.setValue(this.aktuellBis);

        this.fireEvent();
    }

    private clampToTodayIfNeeded(): void {
        if (!this.disableFutureDates) {
            return;
        }
        const todayStart = this.startOfDay(new Date());
        const todayEnd = this.endOfDay(new Date());
        if (this.aktuellBis && this.startOfDay(this.aktuellBis).getTime() > todayStart.getTime()) {
            this.aktuellBis = todayEnd;
        }
        if (this.aktuellVon && this.startOfDay(this.aktuellVon).getTime() > todayStart.getTime()) {
            this.aktuellVon = todayStart;
        }
        if (this.aktuellVon && this.aktuellBis && this.aktuellVon.getTime() > this.aktuellBis.getTime()) {
            this.aktuellVon = this.startOfDay(this.aktuellBis);
        }
    }

    private startOfDay(d: Date): Date {
        const date = new Date(d);
        date.setHours(0, 0, 0, 0);
        return date;
    }

    private endOfDay(d: Date): Date {
        return this.abends(d);
    }

    differenzInTagen(von: Date, bis: Date): number {
        let diffInMilli = bis?.getTime() - von?.getTime();
        return Math.ceil(diffInMilli / (1000 * 60 * 60 * 24));
    }
    /**
     * Gibt die Quartalsnummer des übergebenen Datums zurück.
     *
     * @param {Date} d Datum
     * @return {number} Quartalsnummer
     * @memberof ZeitauswahlComponent
     */
    getQuartal(d: Date): { von: Date; bis: Date } {
        // Januar = 0, Dezember = 11
        let month = d.getMonth();
        if (month < 3) {
            let von = new Date(d.getFullYear(), 0, 1);
            let bis = new Date(d.getFullYear(), 3, 0);
            return { von: von, bis: bis };
        }
        if (month < 6) {
            let von = new Date(d.getFullYear(), 3, 1);
            let bis = new Date(d.getFullYear(), 6, 0);
            return { von: von, bis: bis };
        }
        if (month < 9) {
            let von = new Date(d.getFullYear(), 6, 1);
            let bis = new Date(d.getFullYear(), 9, 0);
            return { von: von, bis: bis };
        }
        let von = new Date(d.getFullYear(), 9, 1);
        let bis = new Date(d.getFullYear(), 11, 31);
        return { von: von, bis: bis };
    }

    getHalbjahr(d: Date): { von: Date; bis: Date } {
        let month = d.getMonth();
        if (month < 6) {
            let von = new Date(d.getFullYear(), 0, 1);
            let bis = new Date(d.getFullYear(), 6, 0); // 30.06.
            return { von: von, bis: bis };
        }
        let von = new Date(d.getFullYear(), 6, 1);
        let bis = new Date(d.getFullYear(), 11, 31);
        return { von: von, bis: bis };
    }

    selectZeitinterval(event: MatButtonToggleChange) {
        if (event.value === 'tag') {
            this.aktuellerZeitintervalInStunden = 24;
        } else if (event.value === 'woche') {
            this.aktuellerZeitintervalInStunden = 24 * 7;
        } else if (event.value === 'monat') {
            this.aktuellerZeitintervalInStunden = 24 * 30;
        } else if (event.value === 'quartal') {
            this.aktuellerZeitintervalInStunden = 24 * 30 * 3;
        } else if (event.value === 'halbjahr') {
            this.aktuellerZeitintervalInStunden = 24 * 30 * 6;
        } else if (event.value === 'jahr') {
            this.aktuellerZeitintervalInStunden = 24 * 30 * 12;
        } else if (event.value === 'zwei_jahre') {
            this.aktuellerZeitintervalInStunden = 24 * 30 * 24;
        }
        this.aktuellerZeitinterval = event.value;

        this.berechneStartUndEnde();
    }

    /** Rolling last ~24 months ending on the given day (inclusive). */
    getZweiJahre(end: Date): { von: Date; bis: Date } {
        const bis = this.abends(end);
        const von = this.morgens(new Date(bis));
        von.setFullYear(von.getFullYear() - 2);
        von.setDate(von.getDate() + 1);
        return { von, bis };
    }

    private shiftZweiJahre(von: Date, bis: Date, direction: 1 | -1): { von: Date; bis: Date } {
        const nextVon = new Date(von);
        const nextBis = new Date(bis);
        nextVon.setFullYear(nextVon.getFullYear() + 2 * direction);
        nextBis.setFullYear(nextBis.getFullYear() + 2 * direction);
        return { von: this.morgens(nextVon), bis: this.abends(nextBis) };
    }
}
